import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
    FailedPspNotificationDto,
    NonCapturedPaymentDto,
    PaymentOperationsDto,
    RetryFailedPspNotificationsDto,
    UnknownStatePaymentDto,
} from '../../../../generated-api'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'context/AuthContext'
import {
    excludeNonCapturedPaymentsFromWashing,
    retryFailedPspNotifications,
    retryNonCapturedPayments,
} from '../api/commands'
import { safeAsync } from 'utils/safe'
import { useResourceFriendlyPaymentOperations } from '../hooks/usePaymentOperationsApi'

export enum WatchedRowKind {
    psp = 'psp',
    payment = 'payment',
    unknown = 'unknown',
}

export enum WatchedRowStatus {
    processing = 'processing',
    completed = 'completed',
}

export type WatchedRow = {
    id: number
    kind: WatchedRowKind
    initialUpdated: string // ISO timestamp
    status: WatchedRowStatus
    removed?: boolean
}

type PaymentOpsContextType = {
    data?: PaymentOperationsDto
    isLoading: boolean
    isRefreshing: boolean
    isRetrying: boolean
    watchedRows: WatchedRow[]
    excludeFromWashing: (paymentIds: number[]) => void
    retryFailed: (failedPspNotificationIds: number[]) => void
    retryNonCaptured: (paymentIds: number[]) => void
    retryUnknown: (paymentIds: number[]) => void
    cancelRefresh: () => void
    refreshData: () => void
}

const findMatchingItem = (
    data: PaymentOperationsDto,
    row: WatchedRow
): FailedPspNotificationDto | NonCapturedPaymentDto | UnknownStatePaymentDto | undefined => {
    switch (row.kind) {
        case WatchedRowKind.psp:
            return data.failedPspNotifications.find((item) => item.pspNotificationId === row.id)
        case WatchedRowKind.payment:
            return data.nonCapturedPayments.find((item) => item.paymentId === row.id)
        case WatchedRowKind.unknown:
            return data.unknownStatePayments.find((item) => item.paymentId === row.id)
    }
}

const PaymentOpsContext = createContext<PaymentOpsContextType | undefined>(undefined)

export const PaymentOpsProvider = ({ children }: { children: React.ReactNode }) => {
    const { getAccessToken } = useAuth()
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [isRetrying, setIsRetrying] = useState(false)

    const [watchedRows, setWatchedRows] = useState<WatchedRow[]>([])
    const { data, isLoading } = useResourceFriendlyPaymentOperations()

    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const countRef = useRef(0)

    const queryClient = useQueryClient()

    const cancelRefresh = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current)
        }

        timerRef.current = null
        countRef.current = 0
        setIsRefreshing(false)
    }, [])

    const refreshData = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }

        countRef.current = 0
        setIsRefreshing(true)

        const run = () => {
            queryClient.invalidateQueries(['payment-operations-updated'])
            countRef.current++

            if (countRef.current >= 10) {
                cancelRefresh()
            }
        }

        run()

        timerRef.current = setInterval(run, 5000)
    }, [cancelRefresh, queryClient])

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
        }
    }, [])

    const updateLocalPaymentOperations = useCallback(
        (newData: PaymentOperationsDto) => {
            queryClient.setQueryData(['payment-operations-updated'], newData)
        },
        [queryClient]
    )

    const markWatched = (rows: { id: number; kind: WatchedRowKind; updated: string }[]) => {
        setWatchedRows((prev) => {
            const previousRowsWithoutNewRows = prev.filter((r) => {
                const match = rows.find((newRow) => newRow.id === r.id && newRow.kind === r.kind)
                return !match
            })

            return [
                ...previousRowsWithoutNewRows,
                ...rows.map((r) => ({
                    id: r.id,
                    kind: r.kind,
                    initialUpdated: r.updated,
                    status: WatchedRowStatus.processing,
                })),
            ]
        })
    }

    useEffect(() => {
        if (!data) {
            return
        }

        setWatchedRows((prev) =>
            prev.map((row) => {
                const match = findMatchingItem(data, row)

                if (!match) {
                    return { ...row, status: WatchedRowStatus.completed, removed: true }
                }

                if (new Date(match.updated) > new Date(row.initialUpdated)) {
                    return { ...row, status: WatchedRowStatus.completed }
                }

                return row
            })
        )
    }, [data])

    const retryFailed = useCallback(
        async (failedPspNotificationIds: number[]) => {
            const token = await getAccessToken()

            if (!token) {
                return
            }

            setIsRetrying(true)

            const watched = failedPspNotificationIds.map((id) => {
                const current = queryClient.getQueryData<PaymentOperationsDto>(['payment-operations-updated'])
                const item = current?.failedPspNotifications.find((x) => x.pspNotificationId === id)
                return {
                    id,
                    kind: WatchedRowKind.psp,
                    updated: item?.updated?.toISOString() ?? new Date().toISOString(),
                }
            })

            markWatched(watched)

            const dto: RetryFailedPspNotificationsDto = {
                pspNotificationIds: failedPspNotificationIds,
            }

            const [result] = await safeAsync(retryFailedPspNotifications(dto, token))

            if (result) {
                updateLocalPaymentOperations(result)
            }

            setIsRetrying(false)
            refreshData()
        },
        [getAccessToken, queryClient, refreshData, updateLocalPaymentOperations]
    )

    const retryNonCaptured = useCallback(
        async (paymentIds: number[]) => {
            const token = await getAccessToken()

            if (!token) {
                return
            }

            const watched = paymentIds.map((id) => {
                const current = queryClient.getQueryData<PaymentOperationsDto>(['payment-operations-updated'])
                const item = current?.failedPspNotifications.find((x) => x.pspNotificationId === id)
                return {
                    id,
                    kind: WatchedRowKind.payment,
                    updated: item?.updated?.toISOString() ?? new Date().toISOString(),
                }
            })

            markWatched(watched)

            const [result] = await safeAsync(retryNonCapturedPayments(paymentIds, token))

            if (result) {
                updateLocalPaymentOperations(result)
            }

            refreshData()
        },
        [getAccessToken, queryClient, refreshData, updateLocalPaymentOperations]
    )

    const retryUnknown = useCallback(
        async (paymentIds: number[]) => {
            const token = await getAccessToken()
            if (!token) {
                return
            }

            const watched = paymentIds.map((id) => {
                const current = queryClient.getQueryData<PaymentOperationsDto>(['payment-operations-updated'])
                const item = current?.unknownStatePayments.find((x) => x.paymentId === id)
                return {
                    id,
                    kind: WatchedRowKind.unknown,
                    updated: item?.updated?.toISOString() ?? new Date().toISOString(),
                }
            })
            markWatched(watched)
            const [result] = await safeAsync(retryNonCapturedPayments(paymentIds, token))
            if (result) {
                updateLocalPaymentOperations(result)
            }

            refreshData()
        },
        [getAccessToken, queryClient, refreshData, updateLocalPaymentOperations]
    )

    const excludeFromWashing = useCallback(
        async (paymentIds: number[]) => {
            const token = await getAccessToken()

            if (!token) {
                return
            }

            const [result] = await safeAsync(excludeNonCapturedPaymentsFromWashing(paymentIds, token))

            if (result) {
                updateLocalPaymentOperations(result)
            }
        },
        [getAccessToken, updateLocalPaymentOperations]
    )

    const result = {
        data,
        isLoading,
        isRefreshing,
        refreshData,
        cancelRefresh,
        retryFailed,
        retryNonCaptured,
        retryUnknown,
        isRetrying,
        excludeFromWashing,
        watchedRows,
    }

    return <PaymentOpsContext.Provider value={result}>{children}</PaymentOpsContext.Provider>
}

export const usePaymentOperations = () => {
    const ctx = useContext(PaymentOpsContext)
    if (!ctx) {
        throw new Error('usePaymentOperations must be used within <PaymentOpsProvider>')
    }
    return ctx
}
